import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, AlertCircle, Clock, CheckCircle2 } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import { formatarCnj } from "@/lib/masks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Importação de processos pela OAB.
//
// O advogado informa a OAB e vai embora. A consulta ao PJe leva minutos — uma
// OAB pode ter centenas de processos, e a API oscila —, então prender a tela a
// ela seria prender o advogado ao tribunal. O pedido entra numa fila, um
// worker executa, e o aviso chega por e-mail e aqui.

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SE","SP","TO"];

const ROTULOS = {
  na_fila: { texto: "Na fila", cor: "text-[#335480] bg-[#F4F7FB]", Icone: Clock },
  processando: { texto: "Consultando o PJe…", cor: "text-[#003882] bg-[#e5edf5]", Icone: Loader2 },
  aguardando_selecao: { texto: "Escolha os processos", cor: "text-[#003882] bg-[#e5edf5]", Icone: CheckCircle2 },
  concluida: { texto: "Concluída", cor: "text-emerald-700 bg-emerald-50", Icone: CheckCircle2 },
  erro: { texto: "Falhou", cor: "text-amber-800 bg-amber-50", Icone: AlertCircle },
};

// Um processo sem movimentação há mais de um ano provavelmente não é o que o
// advogado quer acompanhar. Serve de sugestão inicial, não de regra: ele
// desmarca e marca o que quiser.
const MESES_PARA_SUGERIR = 12;

const recente = (iso) => {
  if (!iso) return false;
  const quando = new Date(iso);
  if (isNaN(quando.getTime())) return false;
  const limite = new Date();
  limite.setMonth(limite.getMonth() - MESES_PARA_SUGERIR);
  return quando >= limite;
};


export default function BuscaPorOab({ open, onOpenChange, onImportado }) {
  const [oabs, setOabs] = useState([]);
  const [numero, setNumero] = useState("");
  const [uf, setUf] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [importacoes, setImportacoes] = useState([]);
  const [preCadastro, setPreCadastro] = useState([]);
  const [selecionando, setSelecionando] = useState(false);
  const [marcados, setMarcados] = useState(() => new Set());
  const [soRecentes, setSoRecentes] = useState(true);
  const [confirmando, setConfirmando] = useState(false);

  const carregar = async () => {
    try {
      const [pedidos, antessala] = await Promise.all([
        axios.get(`${API}/processos/importacoes-oab`, { withCredentials: true }),
        axios.get(`${API}/processos/pre-cadastro`, { withCredentials: true }),
      ]);
      setImportacoes(pedidos.data?.importacoes || []);
      setPreCadastro(antessala.data?.processos || []);
      return pedidos.data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/oabs`, { withCredentials: true })
      .then((res) => {
        const lista = res.data?.oabs || res.data || [];
        setOabs(lista);
        if (lista.length > 0 && !numero) {
          setNumero(lista[0].numero || "");
          setUf(lista[0].uf || "");
        }
      })
      .catch(() => setOabs([]));
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Enquanto houver pedido em andamento, acompanha sozinho: o advogado não
  // deve precisar recarregar para descobrir que terminou.
  useEffect(() => {
    if (!open) return;
    const andando = importacoes.some((i) => i.status === "na_fila" || i.status === "processando");
    if (!andando) return;
    const id = setInterval(carregar, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, importacoes]);

  const abrirSelecao = () => {
    setSelecionando(true);
    // Pré-marca só os movimentados recentemente: é o palpite certo na maioria
    // das vezes, e desmarcar é mais rápido que marcar um a um.
    setMarcados(new Set(
      preCadastro.filter((p) => recente(p.capa?.ultima_movimentacao)).map((p) => p.numero_cnj),
    ));
  };

  const decidir = async (acao) => {
    if (marcados.size === 0) return;
    setConfirmando(true);
    try {
      const res = await axios.post(
        `${API}/processos/pre-cadastro/${acao}`,
        { numeros: Array.from(marcados) },
        { withCredentials: true },
      );
      if (acao === "importar") {
        const { total_importados: total, total_sem_espaco: semEspaco } = res.data || {};
        toast.success(`${total} processo(s) movido(s) para os seus processos`);
        if (semEspaco) {
          toast.warning(`${semEspaco} não couberam no limite do seu plano e continuam no pré-cadastro.`);
        }
      } else {
        toast.success(`${res.data?.dispensados || 0} processo(s) dispensado(s)`);
      }
      setMarcados(new Set());
      const restantes = res.data?.restantes ?? 0;
      if (restantes === 0) setSelecionando(false);
      await carregar();
      onImportado?.();
    } catch (falha) {
      toast.error(falha?.response?.data?.detail || "Não foi possível concluir agora.");
    } finally {
      setConfirmando(false);
    }
  };

  const pedir = async (e) => {
    e?.preventDefault();
    if (!numero.trim() || !uf.trim()) {
      setErro("Informe número e UF da OAB.");
      return;
    }
    setEnviando(true);
    setErro("");
    try {
      const res = await axios.post(
        `${API}/processos/importar-por-oab`,
        { numero: numero.trim(), uf: uf.trim() },
        { withCredentials: true },
      );
      toast.success(res.data?.message || "Importação solicitada.");
      await carregar();
      onImportado?.();
    } catch (falha) {
      setErro(falha?.response?.data?.detail || "Não foi possível solicitar agora.");
    } finally {
      setEnviando(false);
    }
  };

  const resumo = (i) => {
    // O motivo técnico (RuntimeError, HTTPStatusError) não ajuda o advogado:
    // ele não tem como agir sobre isso, e ler o nome da exceção só reforça a
    // sensação de que o sistema quebrou.
    if (i.status === "erro") return "O PJe não respondeu. Seu cadastro está correto — tente de novo em alguns minutos.";
    if (i.status === "aguardando_selecao") {
      return `${i.total_encontrados || 0} encontrados · ${i.total_em_pre_cadastro || 0} na fila`;
    }
    if (i.status !== "concluida") return "Você será avisado por e-mail quando terminar.";
    const partes = [`${i.total_importados || 0} novo(s)`];
    if (i.total_ja_existiam) partes.push(`${i.total_ja_existiam} já cadastrados`);
    if (i.total_sem_acesso) partes.push(`${i.total_sem_acesso} em segredo de justiça`);
    if (i.total_sem_espaco) partes.push(`${i.total_sem_espaco} fora do limite do plano`);
    return partes.join(" · ");
  };

  if (selecionando) {
    const disponiveis = preCadastro;
    const visiveis = soRecentes
      ? disponiveis.filter((p) => recente(p.capa?.ultima_movimentacao))
      : disponiveis;
    const ocultos = disponiveis.length - visiveis.length;

    const alternar = (cnj) => setMarcados((anterior) => {
      const proximo = new Set(anterior);
      proximo.has(cnj) ? proximo.delete(cnj) : proximo.add(cnj);
      return proximo;
    });

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Fila — {disponiveis.length} processo(s) aguardando
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-[#335480]">
            Estes processos vieram da busca por OAB e <strong>ainda não estão</strong> na sua
            lista de processos. Marque e escolha o que fazer: <strong>mover</strong> para os
            seus processos ou <strong>excluir</strong>. O que você excluir não volta a ser
            oferecido numa próxima busca. O que não decidir agora fica aguardando aqui —
            pode fechar e voltar depois.
          </p>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-[#335480]">
              <input type="checkbox" checked={soRecentes}
                     onChange={(e) => setSoRecentes(e.target.checked)}
                     data-testid="filtro-recentes" />
              Só com movimentação no último ano
              {ocultos > 0 && <span className="text-[#6685AD]">({ocultos} ocultos)</span>}
            </label>
            <button type="button"
                    onClick={() => setMarcados(
                      marcados.size === visiveis.length
                        ? new Set()
                        : new Set(visiveis.map((p) => p.numero_cnj)),
                    )}
                    className="text-sm text-[#003882] underline">
              {marcados.size === visiveis.length ? "Desmarcar todos" : "Marcar todos"}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto border border-[#D1DEEE] rounded-md divide-y divide-[#D1DEEE]"
               data-testid="lista-para-escolher">
            {visiveis.length === 0 && (
              <p className="p-6 text-center text-sm text-[#335480]">
                Nenhum processo disponível com esse filtro.
              </p>
            )}
            {visiveis.map((p) => (
              <label key={p.numero_cnj} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-[#F4F7FB]">
                <input type="checkbox" className="mt-1"
                       checked={marcados.has(p.numero_cnj)}
                       onChange={() => alternar(p.numero_cnj)} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#001533] text-sm">
                    {formatarCnj(p.numero_cnj)}
                    {p.capa?.ativo === false && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#F4F7FB] text-[#6685AD]">arquivado</span>
                    )}
                  </div>
                  <div className="text-xs text-[#335480] truncate">
                    {[p.capa?.classe, p.capa?.orgao_julgador, p.capa?.tribunal].filter(Boolean).join(" · ") || "Sem dados de capa"}
                  </div>
                  <div className="text-xs text-[#6685AD]">
                    {p.capa?.ultima_movimentacao
                      ? `Última movimentação: ${new Date(p.capa.ultima_movimentacao).toLocaleDateString("pt-BR")}`
                      : "Sem movimentação informada"}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3">
            <button type="button" onClick={() => setSelecionando(false)}
                    className="text-sm text-[#335480] underline">
              Voltar
            </button>
            <div className="flex items-center gap-3">
              <Button onClick={() => decidir("dispensar")}
                      disabled={confirmando || marcados.size === 0}
                      data-testid="dispensar-selecionados"
                      variant="outline"
                      className="border-[#D1DEEE] text-[#335480] font-semibold">
                Excluir {marcados.size || ""}
              </Button>
              <Button onClick={() => decidir("importar")}
                      disabled={confirmando || marcados.size === 0}
                      data-testid="confirmar-importacao"
                      className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold">
                {confirmando ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                {confirmando ? "Movendo…" : `Mover ${marcados.size} para meus processos`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Importar processos pela OAB
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[#335480]">
          Informe a OAB e nós buscamos os processos no PJe. A consulta roda em segundo
          plano — você pode fechar esta janela e será avisado por e-mail quando terminar.
        </p>

        <form onSubmit={pedir} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <Label htmlFor="oab-numero" className="text-[#001533]">Número da OAB</Label>
            <Input id="oab-numero" data-testid="busca-oab-numero" value={numero}
                   onChange={(e) => setNumero(e.target.value)} placeholder="123456" className="mt-1" />
          </div>
          <div className="w-28">
            <Label htmlFor="oab-uf" className="text-[#001533]">UF</Label>
            <select id="oab-uf" data-testid="busca-oab-uf" value={uf}
                    onChange={(e) => setUf(e.target.value.toUpperCase())}
                    className="mt-1 w-full h-10 rounded-md border border-[#D1DEEE] bg-white px-3 text-sm text-[#001533]">
              <option value="">UF</option>
              {UFS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button type="submit" disabled={enviando} data-testid="busca-oab-submit"
                  className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-10 px-5">
            {enviando ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {enviando ? "Enviando…" : "Importar"}
          </Button>
        </form>

        {oabs.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {oabs.map((o) => (
              <button key={o.oab_id} type="button"
                      onClick={() => { setNumero(o.numero || ""); setUf(o.uf || ""); }}
                      className="px-3 py-1 rounded-full border border-[#D1DEEE] text-xs text-[#335480] hover:border-[#003882]">
                {o.numero}/{o.uf}
              </button>
            ))}
          </div>
        )}

        {erro && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="busca-oab-erro">
            <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-900">{erro}</p>
          </div>
        )}

        {preCadastro.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-[#003882]/25 bg-[#e5edf5] p-3">
            <p className="text-sm text-[#001533]">
              <strong>{preCadastro.length} processo(s)</strong> na fila, aguardando sua decisão.
              Eles não contam no limite do seu plano enquanto estiverem aqui.
            </p>
            <a href="/pre-cadastro" data-testid="abrir-pre-cadastro"
               className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-9 px-4 text-sm shrink-0 rounded-md inline-flex items-center">
              Revisar
            </a>
          </div>
        )}

        {importacoes.length > 0 && (
          <div className="flex-1 overflow-y-auto border border-[#D1DEEE] rounded-md divide-y divide-[#D1DEEE]"
               data-testid="lista-importacoes">
            {importacoes.map((i) => {
              const r = ROTULOS[i.status] || ROTULOS.na_fila;
              return (
                <div key={i.importacao_id} className="flex items-start gap-3 p-3">
                  <r.Icone size={16}
                           className={`mt-0.5 shrink-0 ${i.status === "processando" ? "animate-spin" : ""} ${r.cor.split(" ")[0]}`}
                           aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#001533] text-sm">{i.numero}/{i.uf}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.cor}`}>{r.texto}</span>
                    </div>
                    <div className="text-xs text-[#335480] mt-0.5">{resumo(i)}</div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
